import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Sparkles, GamepadIcon } from "lucide-react";

export default function SetupNamePage() {
  const [playerName, setPlayerName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { setPlayerName: savePlayerName, user } = useAuth();
  const navigate = useNavigate();

  // Name validation
  const nameValid = playerName.length >= 3 && playerName.length <= 15;
  const nameAlphanumeric = /^[a-zA-Z0-9_]+$/.test(playerName);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nameValid) {
      toast.error("Name must be 3-15 characters");
      return;
    }

    if (!nameAlphanumeric) {
      toast.error("Name can only contain letters, numbers, and underscores");
      return;
    }

    setIsLoading(true);
    
    const result = await savePlayerName(playerName);
    
    if (result.success) {
      toast.success(`Welcome, ${playerName}! Let's crush some fruits!`);
      navigate("/map");
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen game-gradient-bg pattern-dots flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-playful border-2 border-border">
        <CardHeader className="text-center space-y-2">
          {/* Logo */}
          <div className="flex justify-center mb-2">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center">
                <GamepadIcon className="w-10 h-10 text-accent" />
              </div>
              <Sparkles className="absolute -top-1 -right-1 w-6 h-6 text-warning animate-sparkle" />
            </div>
          </div>
          
          <CardTitle className="font-display text-3xl font-bold text-foreground">
            Choose Your Name
          </CardTitle>
          
          <CardDescription className="font-body text-muted-foreground">
            Pick a unique player name for the leaderboard!
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Player Name Field */}
            <div className="space-y-2">
              <Label htmlFor="playerName" className="font-body font-medium">
                Player Name
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="playerName"
                  type="text"
                  placeholder="FruitMaster123"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  className="pl-10 font-body text-lg"
                  disabled={isLoading}
                  maxLength={15}
                />
              </div>
              <p className="text-xs text-muted-foreground font-body">
                {playerName.length}/15 characters
              </p>
            </div>

            {/* Name Preview */}
            {playerName && (
              <div className="p-4 bg-secondary rounded-xl text-center">
                <p className="font-body text-sm text-muted-foreground mb-1">
                  Your profile will show:
                </p>
                <p className="font-display text-2xl font-bold text-foreground">
                  {playerName}
                </p>
              </div>
            )}

            {/* Requirements */}
            <div className="space-y-1 p-3 bg-muted rounded-lg">
              <p className="font-body text-sm font-medium text-muted-foreground mb-2">
                Name requirements:
              </p>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${nameValid ? 'bg-success' : 'bg-muted-foreground'}`} />
                <span className={`text-sm font-body ${nameValid ? 'text-success' : 'text-muted-foreground'}`}>
                  3-15 characters
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${nameAlphanumeric || !playerName ? 'bg-success' : 'bg-destructive'}`} />
                <span className={`text-sm font-body ${nameAlphanumeric || !playerName ? 'text-muted-foreground' : 'text-destructive'}`}>
                  Letters, numbers, and underscores only
                </span>
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              size="lg"
              className="w-full font-display font-semibold text-lg bg-accent hover:bg-accent/90 text-accent-foreground shadow-button"
              disabled={isLoading || !nameValid || !nameAlphanumeric}
            >
              {isLoading ? (
                <span className="animate-pulse">Saving...</span>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Start Playing!
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
